import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminPageHeader from '@/components/layouts/AdminPageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';

// Settings sections
import AccountSettings from '@/components/settings/AccountSettings';
import NotificationsSettings from '@/components/settings/NotificationsSettings';
import BillingSettings from '@/components/settings/BillingSettings';
import ApiKeysSettings from '@/components/settings/ApiKeysSettings';
import WorkspaceSettings from '@/components/settings/WorkspaceSettings';
import CompanySettings from '@/components/settings/CompanySettings';

const Settings = () => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeSection, setActiveSection] = useState(tabFromUrl || 'account');
  const { subscriptionTier, trialEndsAt, subscriptionEnd, openCustomerPortal } = useSubscription();

  // Update active section when URL param changes
  useEffect(() => {
    if (tabFromUrl && ['account', 'notifications', 'billing', 'company', 'api-keys', 'workspace'].includes(tabFromUrl)) {
      setActiveSection(tabFromUrl);
    }
  }, [tabFromUrl]);

  const getTrialDaysRemaining = () => {
    if (!trialEndsAt) return 0;
    const diff = new Date(trialEndsAt).getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const daysRemaining = getTrialDaysRemaining();
  const isEffectivelyExpired = subscriptionTier === 'expired' || (subscriptionTier === 'trial' && daysRemaining <= 0);
  const isActiveTrial = subscriptionTier === 'trial' && daysRemaining > 0;

  const getStatusBadge = () => {
    if (subscriptionTier === 'pro') {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Pro · Active</Badge>;
    }
    if (isActiveTrial) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Trial · {daysRemaining} days left</Badge>;
    }
    if (subscriptionTier === 'free') {
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Partner</Badge>;
    }
    if (isEffectivelyExpired) {
      return <Badge className="bg-red-100 text-red-700 border-red-200">Expired</Badge>;
    }
    return null;
  };

  const sections = [
    { id: 'account', label: 'Account', component: AccountSettings },
    { id: 'notifications', label: 'Notifications', component: NotificationsSettings },
    { id: 'billing', label: 'Billing', component: BillingSettings },
    { id: 'company', label: 'Company', component: CompanySettings },
    { id: 'api-keys', label: 'API Keys', component: ApiKeysSettings },
    { id: 'workspace', label: 'Workspace', component: WorkspaceSettings },
  ];

  return (
    <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-8 md:px-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
              <p className="text-muted-foreground mt-1">
                Manage your account preferences and application settings
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-primary" />
                {getStatusBadge()}
              </div>
              {subscriptionTier === 'pro' && (
                <Button variant="outline" size="sm" onClick={() => openCustomerPortal()}>
                  Manage
                </Button>
              )}
            </div>
          </div>

          {/* Tabs Navigation */}
          <Tabs value={activeSection} onValueChange={setActiveSection}>
            <ScrollArea className="w-full">
              <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0 gap-0">
                {sections.map((section) => (
                  <TabsTrigger
                    key={section.id}
                    value={section.id}
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-muted-foreground data-[state=active]:text-foreground hover:text-foreground transition-colors"
                  >
                    {section.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ScrollBar orientation="horizontal" className="invisible" />
            </ScrollArea>

            {/* Tab Content */}
            <div className="mt-8">
              {sections.map((section) => (
                <TabsContent key={section.id} value={section.id} className="mt-0">
                  <section.component />
                </TabsContent>
              ))}
            </div>
          </Tabs>
      </div>
    </div>
  );
};

export default Settings;
